/**
 * FauxDS Modal
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Modal component for prototyping.
 * It is NOT the real @faire/slate Modal and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Modal, see: @faire/slate/components/modal
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback when the modal should close
   */
  onClose: () => void;
  /**
   * Modal content
   */
  children: React.ReactNode;
  /**
   * Modal title (optional)
   */
  title?: string;
  /**
   * Whether clicking the overlay should close the modal
   * @default true
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether pressing Escape should close the modal
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * Optional className for the modal content
   */
  className?: string;
  /**
   * Optional footer content (e.g. action buttons). When provided, the modal uses
   * a flex layout so the main content scrolls and the footer stays sticky at the bottom.
   */
  footer?: React.ReactNode;
}

// Close icon
const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Modal - FauxDS (recreated from Slate)
 *
 * Modals display content that requires user attention or interaction,
 * appearing on top of the main content.
 * This is a simplified implementation for prototyping purposes.
 */
export default function Modal({
  open,
  onClose,
  children,
  title,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = "",
  footer,
}: ModalProps) {
  // Handle Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Lock body scroll and add event listeners when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleKeyDown]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      className={cn(
        "fixed inset-0 z-[9999] grid place-items-center md:place-items-center"
      )}
      style={{
        // Overlay background
        backgroundColor: "rgba(51, 51, 51, 0.5)",
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      {/* Modal content */}
      <div
        className={cn(
          "bg-fs-surface-primary relative mx-4 flex max-h-[90vh] w-full max-w-[480px] min-w-[320px] animate-[modal-slide-in_0.3s_ease-out] flex-col rounded-[8px] p-[24px]",
          footer ? "overflow-hidden" : "overflow-auto",
          className
        )}
        style={{
          boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className={cn(
            "absolute top-[16px] right-[16px] flex h-[32px] w-[32px] items-center justify-center rounded-full bg-transparent text-[#333] transition-colors duration-150 ease-in-out hover:bg-[#f5f5f5] focus:outline-hidden"
          )}
          aria-label="Close modal"
        >
          <CloseIcon />
        </button>

        {/* Title */}
        {title && (
          <h2
            id="modal-title"
            className="mb-[16px] shrink-0 pr-10"
            style={{
              fontFamily:
                "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
              fontSize: "22px",
              fontWeight: 500,
              lineHeight: "28px",
              letterSpacing: "0.15px",
              color: "#333",
            }}
          >
            {title}
          </h2>
        )}

        {/* Content: scrollable when footer is present; flex-1 min-h-0 lets it shrink so footer stays visible */}
        <div
          className={footer ? "min-h-0 flex-1 overflow-auto" : ""}
          style={{
            fontFamily:
              "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
            fontSize: "14px",
            lineHeight: "20px",
            color: "#333",
          }}
        >
          {children}
        </div>

        {/* Sticky footer */}
        {footer && <div className="mt-6 shrink-0">{footer}</div>}
      </div>

      {/* CSS Animation keyframes */}
      <style>{`
        @keyframes modal-slide-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );

  // Render in portal to escape any parent stacking contexts
  return createPortal(modalContent, document.body);
}
